import { readPref, type PrefStorage } from '../shared/clientPrefs';

export type Direction = 'ltr' | 'rtl';

export const SUPPORTED_LANGUAGES = [
  'en',
  'af', 'am', 'ar', 'az', 'be', 'bg', 'bn', 'ca', 'cs', 'da', 'de', 'el', 'es', 'es-MX', 'et', 'eu', 'fa', 'fi', 'fil', 'fr', 'fr-CA',
  'gl', 'gu', 'hi', 'hr', 'hu', 'hy', 'id', 'is', 'it', 'ja', 'ka', 'kk', 'ky', 'km', 'kn', 'ko', 'lo', 'lt', 'lv', 'ml', 'mk',
  'mn', 'mr', 'ms', 'my', 'ne', 'nl', 'no', 'or', 'pa', 'pl', 'pt', 'pt-PT', 'rm', 'ro', 'ru', 'si',
  'sk', 'sl', 'sq', 'sr', 'sv', 'sw', 'ta', 'te', 'th', 'tr', 'uk', 'ur', 'vi', 'zu',
  'he', 'zh-Hans', 'zh-Hant',
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const SUPPORTED = new Set<string>(SUPPORTED_LANGUAGES);
const RTL_LANGS = new Set<string>(['ar', 'he', 'fa', 'ur']);

export function isSupportedLanguage(code: string | null | undefined): code is SupportedLanguage {
  return !!code && SUPPORTED.has(code);
}

export function normalizeLanguageTag(rawTag: string | null | undefined): SupportedLanguage | null {
  if (!rawTag) return null;
  const tag = rawTag.trim();
  if (!tag) return null;

  if (isSupportedLanguage(tag)) return tag;

  const lower = tag.toLowerCase();
  if (lower === 'zh' || lower.startsWith('zh-cn') || lower.startsWith('zh-sg') || lower.startsWith('zh-my') || lower.startsWith('zh-hans')) {
    return 'zh-Hans';
  }
  if (lower.startsWith('zh-tw') || lower.startsWith('zh-hk') || lower.startsWith('zh-mo') || lower.startsWith('zh-hant')) {
    return 'zh-Hant';
  }
  if (lower === 'nb' || lower === 'nn' || lower.startsWith('nb-') || lower.startsWith('nn-') || lower.startsWith('no-')) return 'no';
  if (lower === 'in' || lower.startsWith('in-')) return 'id';
  if (lower === 'iw' || lower.startsWith('iw-')) return 'he';

  const primary = lower.split('-')[0];
  return isSupportedLanguage(primary) ? primary : null;
}

export function getDirection(lang: string): Direction {
  return RTL_LANGS.has(lang) ? 'rtl' : 'ltr';
}

export function detectInitialLanguage(cookieSource = document.cookie, nav: Navigator = navigator, storage?: PrefStorage | null): SupportedLanguage {
  // Cookie-first mit localStorage-Fallback (readPref): iOS persistiert
  // capacitor://-Cookies nicht ueber Kaltstarts — siehe shared/clientPrefs.ts.
  const storedLang = normalizeLanguageTag(readPref('ff_lang', cookieSource, storage));
  if (storedLang) return storedLang;

  return normalizeLanguageTag(nav.language) ?? 'en';
}
